import axios from '../../../axiosConfig';


export const fetchUnifiedData = async (selectedItems) => {
  const econRequests = selectedItems
    .filter(i => i.type === 'economic')
    .map(i => {
      const req = { function: i.code };
      if (i.interval) req.interval = i.interval;
      if (i.maturity) req.maturity = i.maturity;
      return req;
    });

  const commoditySymbols = selectedItems
    .filter(i => i.type === 'commodity')
    .map(i => i.code);

  const companySymbols = selectedItems
    .filter(i => i.type === 'company')
    .map(i => i.code);

  const fxPairs = selectedItems
    .filter(i => i.type === 'fx')
    .map(i => i.code);

  const cryptoPairs = selectedItems
    .filter(i => i.type === 'crypto')
    .map(i => i.code);

  const calls = [];

  // Economics (optional)
  if (econRequests.length > 0) {
    calls.push(
      axios.post('/api/economics_batch_us/', { requests: econRequests })
        .catch(error => {
          console.error('Economics API error:', error);
          errors.push('Failed to fetch economic data. Please check your connection and try again.');
          return { data: {} };
        })
    );
  } else {
    calls.push(Promise.resolve({ data: {} })); // keep tuple order consistent
  }

  // Commodities (optional)
  if (commoditySymbols.length > 0) {
    calls.push(
      axios.get('/get_commodity_data/', {
        params: { symbols: commoditySymbols.join(',') }
      }).catch(error => {
        console.error('Commodities API error:', error);
        errors.push('Failed to fetch commodity data. Please check your connection and try again.');
        return { data: {} };
      })
    );
  } else {
    calls.push(Promise.resolve({ data: {} }));
  }

  // Companies (optional)
  if (companySymbols.length > 0) {
    const companyPromises = companySymbols.map(symbol => 
      axios.get('/api/financial-data-price/', {
        params: { 
          function: 'TIME_SERIES_DAILY_ADJUSTED',
          symbol: symbol,
          outputsize: 'full'
        }
      }).catch(error => {
        console.error(`Company API error for ${symbol}:`, error);
        return { data: null };
      })
    );
    calls.push(Promise.all(companyPromises));
  } else {
    calls.push(Promise.resolve({ data: {} }));
  }

  // Foreign Exchange (optional)
  if (fxPairs.length > 0) {
    const fxPromises = fxPairs.map(pair => {
      const [fromSymbol, toSymbol] = pair.split('/');
      return axios.get(`/get_fx_data/${fromSymbol}/${toSymbol}/`).catch(error => {
        console.error(`FX API error for ${pair}:`, error);
        return { data: null };
      });
    });
    calls.push(Promise.all(fxPromises));
  } else {
    calls.push(Promise.resolve({ data: {} }));
  }

  // Cryptocurrency (optional)
  if (cryptoPairs.length > 0) {
    const cryptoPromises = cryptoPairs.map(pair => {
      const [fromSymbol, toSymbol] = pair.split('/');
      return axios.get(`/get_crypto_data/${fromSymbol}/${toSymbol}/`).catch(error => {
        console.error(`Crypto API error for ${pair}:`, error);
        return { data: null };
      });
    });
    calls.push(Promise.all(cryptoPromises));
  } else {
    calls.push(Promise.resolve({ data: {} }));
  }

  const [econRes, comRes, companyRes, fxRes, cryptoRes] = await Promise.all(calls);
  
  // Track failed/missing data items
  const errors = [];
  
  // Process company data into a cleaner format
  let processedCompanyData = {};
  if (companySymbols.length > 0 && companyRes) {
    companySymbols.forEach((symbol, index) => {
      if (companyRes[index] && companyRes[index].data && 
          companyRes[index].data['Time Series (Daily)'] && 
          Object.keys(companyRes[index].data['Time Series (Daily)']).length > 0) {
        processedCompanyData[symbol] = companyRes[index].data;
      } else {
        errors.push(`Company ticker '${symbol}' is not available.`);
      }
    });
  }

  // Process FX data into a cleaner format
  let processedFxData = {};
  if (fxPairs.length > 0 && fxRes) {
    fxPairs.forEach((pair, index) => {
      if (fxRes[index] && fxRes[index].data && 
          fxRes[index].data['Time Series FX (Daily)'] && 
          Object.keys(fxRes[index].data['Time Series FX (Daily)']).length > 0) {
        processedFxData[pair] = fxRes[index].data;
      } else {
        errors.push(`FX pair '${pair}' is not available.`);
      }
    });
  }

  // Process Crypto data into a cleaner format
  let processedCryptoData = {};
  if (cryptoPairs.length > 0 && cryptoRes) {
    cryptoPairs.forEach((pair, index) => {
      if (cryptoRes[index] && cryptoRes[index].data && 
          cryptoRes[index].data['Time Series (Digital Currency Daily)'] && 
          Object.keys(cryptoRes[index].data['Time Series (Digital Currency Daily)']).length > 0) {
        processedCryptoData[pair] = cryptoRes[index].data;
      } else {
        errors.push(`Crypto pair '${pair}' is not available.`);
      }
    });
  }

  // Check economics data
  if (econRequests.length > 0 && econRes && econRes.data) {
    econRequests.forEach((req, index) => {
      const functionName = req.function;
      if (!econRes.data[functionName] || !econRes.data[functionName].data || 
          econRes.data[functionName].data.length === 0) {
        errors.push(`Economic indicator '${functionName}' is not currently available. Please try a different indicator.`);
      }
    });
  }

  // Check commodity data
  if (commoditySymbols.length > 0 && comRes && comRes.data) {
    commoditySymbols.forEach(symbol => {
      if (!comRes.data[symbol] || !comRes.data[symbol].data || 
          comRes.data[symbol].data.length === 0) {
        errors.push(`Commodity '${symbol}' is not currently available. Please try a different commodity.`);
      }
    });
  }
  
  return { 
    econData: econRes.data, 
    comData: comRes.data,
    companyData: processedCompanyData,
    fxData: processedFxData,
    cryptoData: processedCryptoData,
    errors: errors
  };
};
